/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import {
	type ComponentRef,
	Injectable,
	Injector,
	type Type,
	type ViewContainerRef,
	inject,
} from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { ActionLoggerService } from "@app/@services/generic/action-logger.service";
import { sha256 } from "js-sha256";
import { Subject, debounceTime, filter, take, tap } from "rxjs";
import { DIALOG_REGISTRY } from "./dialog-registry";
import { CbDialogConfig, CbDialogRef } from "./dialog.class";
import { DialogComponent } from "./dialog/dialog.component";
import { FullscreenDialogComponent } from "./fullscreen-dialog/fullscreen-dialog.component";

export interface DialogChangeEvent {
	event: "open" | "close";
	dialogRef: CbDialogRef;
	dialogConfig: CbDialogConfig<any> | null;
}

@Injectable({
	providedIn: "root",
})
export class DialogService {
	private readonly actionLoggerService: ActionLoggerService =
		inject(ActionLoggerService);

	// Injection dei servizi per la gestione URL
	private readonly router = inject(Router, { optional: true });
	private readonly route = inject(ActivatedRoute, { optional: true });

	public readonly dialogChanges: Subject<DialogChangeEvent> =
		new Subject<DialogChangeEvent>();

	public readonly escKeyPressed: Subject<void> = new Subject<void>();
	public readonly backNavigationTriggered: Subject<void> = new Subject<void>();

	/**
	 * Map of dialog references to dialog component references.
	 */
	public readonly dialogComponentRefMap: Map<
		CbDialogRef,
		{
			ref: ComponentRef<any>;
			level: number;
		}
	> = new Map<
		CbDialogRef,
		{
			ref: ComponentRef<any>;
			level: number;
		}
	>();

	private readonly injector: Injector = inject(Injector);

	private vcr?: ViewContainerRef;

	// Tracking URL
	private isHandlingUrl = false;
	private currentDialogId: string | null = null;
	private isDialogOpenedFromUrl = false;

	constructor() {
		this.escKeyPressed
			.pipe(
				debounceTime(100),
				tap(() => {
					this.closeLastDialog();
				}),
			)
			.subscribe();

		this.backNavigationTriggered
			.pipe(
				debounceTime(100),
				tap(() => {
					this.closeLastDialog();
				}),
			)
			.subscribe();

		// Inizializza la gestione degli URL
		this.initializeUrlHandling();
	}

	/**
	 * Inizializza la gestione dell'URL per le dialog
	 */
	private initializeUrlHandling(): void {
		if (!this.router || !this.route) return;

		// Gestisce il caso di navigazione diretta a un URL con parametro dialog
		this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe(() => {
				// Controlla se c'è un parametro dialog nell'URL
				this.route?.queryParams
					.pipe(
						take(1), // Prendi solo il primo set di parametri
					)
					.subscribe((params) => {
						// Verifica se c'è un dialogo da aprire e nessun dialogo attualmente visualizzato
						if (
							params["dialog"] &&
							!this.isHandlingUrl &&
							this.dialogComponentRefMap.size === 0
						) {
							this.openDialogFromUrl(params);
						}
					});
			});

		// Registra gli eventi di apertura e chiusura delle dialog
		this.dialogChanges.subscribe((event) => {
			// Quando una dialog viene aperta programmaticamente (non da URL)
			if (
				event.event === "open" &&
				!this.isHandlingUrl &&
				!this.isDialogOpenedFromUrl
			) {
				// Verifica che non ci siano già dialoghi nell'URL e che questo sia il primo dialogo
				if (
					this.dialogComponentRefMap.size === 1 &&
					this.currentDialogId === null
				) {
					setTimeout(() => {
						// Usiamo un timeout per evitare conflitti con eventi di navigazione
						this.updateUrlForDialog(event.dialogRef, event.dialogConfig);
					}, 100);
				}
			}
			// Quando una dialog viene chiusa
			else if (event.event === "close") {
				// Se era la dialog tracciata nell'URL, rimuviamo i parametri
				if (
					this.currentDialogId !== null &&
					this.dialogComponentRefMap.size === 0
				) {
					setTimeout(() => {
						// Usiamo un timeout per assicurarci che la dialog sia completamente chiusa
						this.removeDialogFromUrl();
					}, 100);
				}
			}

			// Resetta il flag dopo l'evento
			if (event.event === "open") {
				setTimeout(() => {
					this.isDialogOpenedFromUrl = false;
				}, 200);
			}
		});
	}

	/**
	 * Apre una dialog dai parametri nell'URL
	 */
	private openDialogFromUrl(params: any): void {
		if (!this.router || !this.route) return;

		// Imposta i flag per prevenire aperture multiple
		this.isHandlingUrl = true;
		this.isDialogOpenedFromUrl = true;

		const dialogId = params["dialog"];

		// Estrai e decodifica sia i dati che la configurazione completa
		let configData = {};
		let dialogConfig: Partial<CbDialogConfig> = { type: "modal" };

		// Estrazione dati
		if (params["data"]) {
			try {
				configData = this.decodeDialogData(params["data"]);
			} catch (error) {
				console.error("Errore nel parsing dei dati della dialog:", error);
			}
		}

		// Estrazione configurazione
		if (params["config"]) {
			try {
				const decodedConfig = this.decodeDialogData(params["config"]);
				dialogConfig = {
					...dialogConfig,
					...decodedConfig,
				};
			} catch (error) {
				console.error(
					"Errore nel parsing della configurazione della dialog:",
					error,
				);
			}
		}

		// Assicurati che i dati siano sempre un oggetto e assegnali alla configurazione
		if (typeof configData === "object" && configData !== null) {
			dialogConfig.data = configData;
		} else {
			dialogConfig.data = {};
			console.warn(
				"I dati decodificati non sono un oggetto valido, usando oggetto vuoto",
			);
		}

		if (dialogId && DIALOG_REGISTRY[dialogId]) {
			this.currentDialogId = dialogId;
			const component = DIALOG_REGISTRY[dialogId];

			// Apre la dialog con la configurazione completa
			const dialogRef = this.open(component, dialogConfig as CbDialogConfig);
			dialogRef.onClose.subscribe(() => {
				// Salva l'ID corrente prima di resettarlo
				const wasCurrentDialogId = this.currentDialogId;
				this.currentDialogId = null;

				// Rimuovi i parametri dall'URL solo se era la dialog corrente
				if (wasCurrentDialogId === dialogId) {
					setTimeout(() => {
						this.removeDialogFromUrl();
					}, 100);
				}
			});
		}

		// Ripristina lo stato, ma manteniamo isDialogOpenedFromUrl a true
		// per evitare che l'evento di apertura della dialog aggiorni nuovamente l'URL
		this.isHandlingUrl = false;
	}

	/**
	 * Aggiorna l'URL con i dati della dialog corrente
	 */
	private updateUrlForDialog(
		dialogRef: CbDialogRef,
		dialogConfig: CbDialogConfig<any> | null,
	): void {
		if (!this.router || !this.route) return;

		// Trova l'ID della dialog dal registry invertendo la mappa
		let dialogId = null;
		const dialogComponentRefMapData = this.dialogComponentRefMap.get(dialogRef);

		if (dialogComponentRefMapData) {
			const component = dialogComponentRefMapData.ref.instance.component;

			for (const [id, comp] of Object.entries(DIALOG_REGISTRY)) {
				if (comp === component) {
					dialogId = id;
					break;
				}
			}
		}

		if (!dialogId) return;

		this.currentDialogId = dialogId;

		// Ottiene i dati della dialog e la configurazione
		const dialogData = dialogConfig?.data || {};

		// Estrai le altre impostazioni di configurazione (escluso i dati)
		const { data, ...configWithoutData } = dialogConfig || {};

		// Codifica separatamente i dati e la configurazione
		const encodedData = this.encodeDialogData(dialogData);
		const encodedConfig = this.encodeDialogData(configWithoutData);

		// Aggiorna l'URL senza causare una navigazione
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: {
				dialog: dialogId,
				data: encodedData,
				config: encodedConfig,
			},
			queryParamsHandling: "merge",
			replaceUrl: true,
		});
	}

	/**
	 * Rimuove i parametri della dialog dall'URL
	 */
	private removeDialogFromUrl(): void {
		if (!this.router || !this.route) return;

		// Ottiene tutti i parametri correnti
		this.route.queryParams
			.pipe(
				take(1), // Prendi solo il primo set di parametri per evitare cicli
			)
			.subscribe((params) => {
				const newParams = { ...params };

				// Rimuove i parametri relativi alla dialog
				delete newParams["dialog"];
				delete newParams["data"];
				delete newParams["type"];
				delete newParams["config"];

				// Aggiorna l'URL solo se necessario (per evitare navigazioni inutili)
				if (params["dialog"]) {
					this.router?.navigate([], {
						relativeTo: this.route,
						queryParams: newParams,
						replaceUrl: true,
					});
				}

				this.currentDialogId = null;
			});
	}

	/**
	 * Codifica i dati della dialog in una stringa URL-safe
	 */
	private encodeDialogData(data: any): string {
		try {
			if (data === undefined || data === null) {
				return "";
			}

			// Rimuovi funzioni e valori circolari
			const cleanData = this.cleanDataForSerialization(data);
			// Base64 encoding per rendere i dati URL-safe
			const jsonStr = JSON.stringify(cleanData);
			const encoded = btoa(encodeURIComponent(jsonStr));
			return encoded;
		} catch (error) {
			console.error("Errore nella codifica dei dati della dialog:", error);
			return "";
		}
	}

	/**
	 * Decodifica i dati della dialog da una stringa URL-safe
	 */
	private decodeDialogData(encodedData: string): any {
		try {
			if (!encodedData) {
				return {};
			}

			const decoded = decodeURIComponent(atob(encodedData));
			const result = JSON.parse(decoded);
			return result;
		} catch (error) {
			console.error("Errore nella decodifica dei dati della dialog:", error, {
				encodedData,
			});
			return {};
		}
	}

	/**
	 * Pulisce i dati per la serializzazione
	 */
	private cleanDataForSerialization(data: any): any {
		if (!data) return {};

		try {
			// Prima convertiamo in stringa JSON e poi riparsiamo
			// Questo elimina funzioni e riferimenti circolari
			return JSON.parse(JSON.stringify(data));
		} catch (error) {
			console.error("Errore nella pulizia dei dati:", error);

			// In caso di errore, tenta di creare una copia pulita manualmente
			if (Array.isArray(data)) {
				return [];
			}

			// Ritorna un oggetto vuoto in caso di fallimento
			return {};
		}
	}

	public get dialogTreeIdentifier(): string {
		let key = "";

		// Iterate over all the dialogComponentRefMap
		// and calculate a unique key based on the component name and the dialog data
		for (const dialogRef of this.dialogComponentRefMap.keys()) {
			const mapData = this.dialogComponentRefMap.get(dialogRef);
			if (mapData?.ref) {
				const name = mapData.ref.instance.component.name;
				const data = JSON.stringify(mapData.ref.instance.dialogConfig.data);

				key += sha256(`${name}-${data}`);
			}
		}

		return key;
	}

	public setViewContainerRef(vcr: ViewContainerRef) {
		this.vcr = vcr;
	}

	/**
	 * Opens a dialog with the specified component.
	 * @param component the component to open in the dialog
	 * @param config the configuration for the dialog
	 * @returns the dialog reference
	 */
	public open(
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		component: Type<any>,
		config: CbDialogConfig = { data: {}, type: "modal" },
	): CbDialogRef {
		if (!this.vcr) throw new Error("ViewContainerRef not set");

		const dialogRef = new CbDialogRef();

		// Create the injector for the dialog component
		const injector = Injector.create({
			providers: [
				{
					provide: CbDialogRef,
					useValue: dialogRef,
				},
				{
					provide: CbDialogConfig,
					useValue: config,
				},
			],
			parent: this.injector,
		});

		const mainComponent: Type<FullscreenDialogComponent | DialogComponent> =
			config.type === "fullscreen"
				? FullscreenDialogComponent
				: DialogComponent;

		// Create the dialog component
		const dialogComponentRef = this.vcr.createComponent(mainComponent, {
			injector: injector,
		});

		// Attach the dialog component to the appRef so that it's inside the ng component tree
		dialogComponentRef.setInput("dialogRef", dialogRef);
		dialogComponentRef.setInput("dialogConfig", config);
		dialogComponentRef.instance.component = component;

		const maxLevel = Math.max(
			...Array.from(this.dialogComponentRefMap.values()).map((v) => v.level),
			-1, // Valore di default se l'array è vuoto
		);

		// Attach the dialog component to the body
		this.dialogComponentRefMap.set(dialogRef, {
			ref: dialogComponentRef,
			level: maxLevel + 1,
		});
		dialogComponentRef.instance.onDestroy.subscribe(() =>
			this.destroyDialog(dialogRef),
		);

		// Emit the dialog open event
		this.dialogChanges.next({ event: "open", dialogRef, dialogConfig: config });

		// Log the dialog open
		const data = config.data;
		// Iterate recursively over the complex data object and remove the empty strings
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		const cleanData = (obj: any) => {
			for (const key in obj) {
				if (typeof obj[key] === "object") {
					cleanData(obj[key]);
				} else if (Array.isArray(obj[key])) {
					for (const item of obj[key]) {
						cleanData(item);
					}
				} else if (
					obj[key] !== null &&
					obj[key] !== undefined &&
					obj[key] !== ""
				) {
					obj[key] = sha256(obj[key].toString());
				}
			}
		};
		//cleanData(data);
		this.actionLoggerService.registerAction(
			`Dialog opened: ${component.name}`,
			data,
		);

		return dialogRef;
	}

	/**
	 * Closes the last opened dialog.
	 */
	public closeLastDialog(): void {
		const maxLevel = Math.max(
			...Array.from(this.dialogComponentRefMap.values()).map((v) => v.level),
			-1, // Valore di default se l'array è vuoto
		);

		const dialogRef = Array.from(this.dialogComponentRefMap.entries()).find(
			([, v]) => v.level === maxLevel,
		)?.[0];

		if (dialogRef) {
			dialogRef.close();
		}

		/* const dialogRef = Array.from(this.dialogComponentRefMap.keys()).pop();

		if (dialogRef) {
			dialogRef.close();
		} */
	}

	/**
	 * Closes all opened dialogs.
	 */
	public closeAllDialogs(): void {
		for (const dialogRef of this.dialogComponentRefMap.keys()) {
			dialogRef.close();
		}
	}

	/**
	 * Destroys the dialog component.
	 * @param dialogRef the dialog reference
	 */
	private destroyDialog(dialogRef: CbDialogRef): void {
		// Destroy the dialog component
		const componentRefMapData = this.dialogComponentRefMap.get(dialogRef);

		if (componentRefMapData) {
			// Remove the dialog component from the appRef
			this.dialogComponentRefMap.delete(dialogRef);

			// Destroy the dialog component
			componentRefMapData.ref.destroy();

			// Emit the dialog close event
			this.dialogChanges.next({
				event: "close",
				dialogRef,
				dialogConfig: null,
			});
		}
	}
}
